import { projectId, publicAnonKey } from '/utils/supabase/info';

const API_BASE = `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4`;

function authHeaders(accessToken: string): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${publicAnonKey}`,
    'X-User-Token': accessToken,
  };
}

export async function getSignatureLabState(accessToken: string): Promise<Record<string, unknown> | null> {
  const res = await fetch(`${API_BASE}/signature-lab/state`, {
    headers: authHeaders(accessToken),
  });
  if (!res.ok) throw new Error('Failed to load Signature Lab state');
  const data = await res.json();
  return (data?.state ?? null) as Record<string, unknown> | null;
}

export async function saveSignatureLabState(
  accessToken: string,
  state: Record<string, unknown>,
): Promise<void> {
  const res = await fetch(`${API_BASE}/signature-lab/state`, {
    method: 'POST',
    headers: authHeaders(accessToken),
    body: JSON.stringify(state),
  });
  if (!res.ok) throw new Error('Failed to save Signature Lab state');
}

export async function createPartnerInvite(accessToken: string, partnerEmail: string): Promise<string> {
  const res = await fetch(`${API_BASE}/signature-lab/partner-invite`, {
    method: 'POST',
    headers: authHeaders(accessToken),
    body: JSON.stringify({ partnerEmail }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || 'Failed to create invite');
  return data.code as string;
}

export async function acceptPartnerConsent(accessToken: string, code: string): Promise<string> {
  const res = await fetch(`${API_BASE}/signature-lab/partner-consent`, {
    method: 'POST',
    headers: authHeaders(accessToken),
    body: JSON.stringify({ code, consent: true }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || 'Failed to accept consent');
  return data.pairId as string;
}

export async function getPartnerStatus(accessToken: string): Promise<Record<string, unknown>> {
  const res = await fetch(`${API_BASE}/signature-lab/partner-status`, {
    headers: authHeaders(accessToken),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || 'Failed to fetch partner status');
  return (data ?? {}) as Record<string, unknown>;
}

export async function saveSharedDelta(
  accessToken: string,
  score: number,
): Promise<{ delta: number | null; pairId: string | null }> {
  const res = await fetch(`${API_BASE}/signature-lab/delta`, {
    method: 'POST',
    headers: authHeaders(accessToken),
    body: JSON.stringify({ score }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || 'Failed to save shared delta');
  return {
    delta: typeof data?.delta === 'number' ? data.delta : null,
    pairId: typeof data?.pairId === 'string' ? data.pairId : null,
  };
}

export async function getSharedDeltaHistory(accessToken: string): Promise<Array<Record<string, unknown>>> {
  const res = await fetch(`${API_BASE}/signature-lab/delta/history`, {
    headers: authHeaders(accessToken),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || 'Failed to fetch shared delta history');
  return Array.isArray(data?.history) ? (data.history as Array<Record<string, unknown>>) : [];
}

export async function getTherapistExport(accessToken: string): Promise<Record<string, unknown>> {
  const res = await fetch(`${API_BASE}/signature-lab/therapist-export`, {
    headers: authHeaders(accessToken),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || 'Failed to build therapist export');
  return (data.report ?? {}) as Record<string, unknown>;
}

export async function saveSignatureLabActionOutcome(
  accessToken: string,
  outcome: { category: string; impact: 'better' | 'same' | 'worse'; notes?: string },
): Promise<void> {
  const res = await fetch(`${API_BASE}/signature-lab/action-outcome`, {
    method: 'POST',
    headers: authHeaders(accessToken),
    body: JSON.stringify(outcome),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || 'Failed to save action outcome');
}

export async function getSignatureLabProgress(accessToken: string): Promise<Record<string, unknown>> {
  const res = await fetch(`${API_BASE}/signature-lab/progression`, {
    headers: authHeaders(accessToken),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || 'Failed to load progression');
  return (data ?? {}) as Record<string, unknown>;
}

export async function saveEIAssessmentSnapshotServer(
  accessToken: string,
  snapshot: Record<string, unknown>,
): Promise<void> {
  const res = await fetch(`${API_BASE}/ei-assessment`, {
    method: 'POST',
    headers: authHeaders(accessToken),
    body: JSON.stringify({ snapshot }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || 'Failed to save EI assessment snapshot');
}

export async function getEIAssessmentSnapshotServer(accessToken: string): Promise<Record<string, unknown> | null> {
  const res = await fetch(`${API_BASE}/ei-assessment`, {
    headers: authHeaders(accessToken),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || 'Failed to load EI assessment snapshot');
  return (data?.snapshot ?? null) as Record<string, unknown> | null;
}
